import Dinero from "dinero.js";

interface MoneyFormatOptions {
  // Formats large numbers in millions (e.g., $1M, $1.5M)
  abbreviateMillions?: boolean;
  // Specifies the format of the input:
  inputFormat?: "dollars" | "cents";
  // Symbol prefixed to the formatted amount; defaults to "$"
  currencySymbol?: string;
}

export function formatMoney(
  amount?: string | number,
  options: MoneyFormatOptions = {}
): string {
  const {
    abbreviateMillions = false,
    inputFormat = "dollars",
    currencySymbol = "$",
  } = options;
  if (!amount) return `${currencySymbol}0.00`;

  // Convert string input to number
  const numericAmount =
    typeof amount === "string"
      ? parseFloat(amount.replace(/[^0-9.-]/g, ""))
      : amount;

  if (isNaN(numericAmount)) {
    throw new Error(`Invalid monetary amount provided for value ${amount}`);
  }

  // Handle millions abbreviation if requested
  if (abbreviateMillions && Math.abs(numericAmount) >= 1000000) {
    const millions = Math.abs(numericAmount) / 1000000;
    const formatted = millions.toFixed(1).replace(".0", "");
    const sign = numericAmount < 0 ? "-" : "";
    return `${sign}${currencySymbol}${formatted}M`;
  }

  // Convert to cents for Dinero
  const cents =
    inputFormat === "cents"
      ? Math.round(numericAmount)
      : Math.round(numericAmount * 100);

  // Sign goes before the symbol (e.g. -€5.00), matching Dinero's "$0,0.00"
  const sign = cents < 0 ? "-" : "";
  const digits = Dinero({ amount: Math.abs(cents) }).toFormat("0,0.00");
  return `${sign}${currencySymbol}${digits}`;
}
